import { useEffect, useRef, useState } from 'react';

/**
 * Clipboard-copy behavior for a copy affordance: writes `value` to the
 * clipboard and flips `copied` true for 1.5s so the trigger can show a
 * confirmation. The chrome is the caller's — pair this with whichever button
 * component fits the surface (PrimaryButton, SmallSecondaryButton, …).
 */
export function useClipboardCopy(value: string) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const copy = async () => {
    if (!navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* denied or insecure context — leave state unchanged */
    }
  };

  return { copied, copy };
}
